import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import { theme } from '../../../styles/theme';
import { ACTIONS } from '../../../hooks/useWizardForm';
import StepHint from '../StepHint';
import AvailableFieldsChips from '../AvailableFieldsChips';

/**
 * StepTemplateVars — Step 3 of the Campaign Wizard.
 * Template selection and variable mapping with available field chips.
 *
 * Props:
 *   state          - wizard form state
 *   dispatch       - reducer dispatch
 *   errors         - validation errors object
 *   mktTemplates   - Array<{ id, name, channel, connectorId, variables }>
 *   connectors     - Array<{ id, name }>
 *   availableFields - string[] derived from useAvailableFields
 */
export default function StepTemplateVars({
  state,
  dispatch,
  errors,
  mktTemplates,
  connectors,
  availableFields,
}) {
  const { t } = useTranslation('campaigns');
  const [lastEditedIndex, setLastEditedIndex] = useState(null);

  // Filter templates by channel and status (matching original approvedTemplates logic)
  const filteredTemplates = useMemo(() => {
    if (!mktTemplates) return [];
    const connector = connectors.find((c) => c.connectorId === state.connectorId);
    // eslint-disable-next-line no-console
    console.log('[StepTemplateVars] filter debug:', {
      channel: state.channel, connectorId: state.connectorId,
      connectorFound: !!connector, waba: connector?.businessAccountId,
      totalTemplates: mktTemplates.length,
    });
    return mktTemplates.filter((tpl) => {
      if (tpl.status !== 'approved') return false;
      if (state.channel && tpl.channel !== state.channel) return false;
      if (state.channel === 'whatsapp') {
        const waba = connector?.businessAccountId;
        if (waba && tpl.businessAccountId) return tpl.businessAccountId === waba;
        return true;
      }
      // Email: match connectorId only when both have it
      if (state.channel === 'email' && state.connectorId && tpl.connectorId) {
        return tpl.connectorId === state.connectorId;
      }
      return true;
    });
  }, [mktTemplates, state.channel, state.connectorId, connectors]);

  // Get the currently selected template
  const selectedTemplate = useMemo(() => {
    if (!state.templateId || !mktTemplates) return null;
    return mktTemplates.find((tpl) => tpl.templateId === state.templateId || tpl.id === state.templateId) || null;
  }, [state.templateId, mktTemplates]);

  const handleTemplateChange = useCallback((e) => {
    const templateId = e.target.value;
    dispatch({ type: ACTIONS.SELECT_TEMPLATE, payload: { templateId } });
  }, [dispatch]);

  const handleVariableChange = useCallback((index, value) => {
    setLastEditedIndex(index);
    dispatch({
      type: ACTIONS.SET_FIELD,
      payload: { path: `variables.${index}`, value },
    });
  }, [dispatch]);

  const handleFieldChipClick = useCallback((fieldName) => {
    if (lastEditedIndex !== null) {
      dispatch({
        type: ACTIONS.SET_FIELD,
        payload: { path: `variables.${lastEditedIndex}`, value: fieldName },
      });
    }
  }, [dispatch, lastEditedIndex]);

  return (
    <Container>
      <StepHint
        icon={FileText}
        title={t('stepTemplate.hintTitle')}
        description={t('stepTemplate.hintDescription')}
      />

      <FormSection>
        <Label htmlFor="template-select">{t('stepTemplate.template')}</Label>
        <Select
          id="template-select"
          value={state.templateId || ''}
          onChange={handleTemplateChange}
          $hasError={!!errors.templateId}
        >
          <option value="">{t('stepTemplate.selectTemplate')}</option>
          {filteredTemplates.map((tpl) => (
            <option key={tpl.templateId || tpl.id} value={tpl.templateId || tpl.id}>
              {tpl.name}
              {tpl.type === 'utility' ? t('stepTemplate.utilitySuffix') : t('stepTemplate.marketingSuffix')}
              {tpl.status && tpl.status !== 'approved' ? ` (${tpl.status})` : ''}
            </option>
          ))}
        </Select>
        {errors.templateId && <ErrorMessage>{errors.templateId}</ErrorMessage>}
      </FormSection>

      {selectedTemplate && selectedTemplate.variables && selectedTemplate.variables.length > 0 && (
        <VariablesSection>
          <SectionTitle>{t('stepTemplate.variableMapping')}</SectionTitle>
          {selectedTemplate.variables.map((variable) => (
            <VariableRow key={variable.index}>
              <VariableBadge>{`{{${variable.index}}}`}</VariableBadge>
              <VariableDescription>{variable.description}</VariableDescription>
              <VariableInput
                type="text"
                placeholder={t('stepTemplate.mappedFieldPlaceholder')}
                value={state.variables?.[variable.index] || ''}
                onChange={(e) => handleVariableChange(variable.index, e.target.value)}
                onFocus={() => setLastEditedIndex(variable.index)}
              />
            </VariableRow>
          ))}

          {availableFields && availableFields.length > 0 ? (
            <ChipsContainer>
              <AvailableFieldsChips
                fields={availableFields}
                onFieldClick={handleFieldChipClick}
                label={t('stepTemplate.availableFieldsLabel')}
              />
            </ChipsContainer>
          ) : (
            <FreeTextHint>
              {t('stepTemplate.freeTextHint')}
            </FreeTextHint>
          )}
        </VariablesSection>
      )}
    </Container>
  );
}

// ─── Styled Components ───────────────────────────────────────────────────────

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[5]};
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.medium};
  color: ${theme.colors.textPrimary};
`;

const Select = styled.select`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${(p) => (p.$hasError ? theme.colors.danger : theme.colors.border)};
  border-radius: ${theme.radii.md};
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textPrimary};
  background: ${theme.colors.white};
  transition: ${theme.transitions.fast};

  &:hover {
    border-color: ${(p) => (p.$hasError ? theme.colors.danger : theme.colors.borderHover)};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px ${theme.colors.primaryLight};
  }
`;

const ErrorMessage = styled.span`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.danger};
`;

const VariablesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const SectionTitle = styled.h4`
  margin: 0;
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
`;

const VariableRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: ${theme.colors.bgSecondary};
  border-radius: ${theme.radii.md};

  @media (max-width: ${theme.breakpoints.md}) {
    flex-direction: column;
    align-items: stretch;
    gap: ${theme.spacing[2]};
  }
`;

const VariableBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  background: ${theme.colors.primaryLight};
  color: ${theme.colors.primary};
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.semibold};
  font-family: ${theme.typography.fontMono};
  border-radius: ${theme.radii.sm};
  white-space: nowrap;
  flex-shrink: 0;
`;

const VariableDescription = styled.span`
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textSecondary};
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const VariableInput = styled.input`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textPrimary};
  background: ${theme.colors.white};
  min-width: 180px;
  transition: ${theme.transitions.fast};

  &:hover {
    border-color: ${theme.colors.borderHover};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px ${theme.colors.primaryLight};
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const ChipsContainer = styled.div`
  margin-top: ${theme.spacing[2]};
`;

const FreeTextHint = styled.p`
  margin: ${theme.spacing[2]} 0 0;
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
  font-style: italic;
`;
